import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "types";

import {
  Box,
  Typography,
  Paper,
  Stack,
  Alert,
  Skeleton,
  Divider,
  Card,
  CardContent,
  Button,
  CircularProgress,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { styled } from "@mui/material/styles";

import PersonIcon from "@mui/icons-material/Person";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import TextEditor from "app/Components/Fields/TextEditor";

import { db } from "config/firebase";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import UploadImage from "./UploadImage";
import UserInfo from "./UserInfo.jsx";

const StyledCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  transition: "box-shadow 0.3s ease-in-out",
  "&:hover": {
    boxShadow: theme.shadows[4],
  },
}));

const BioContainer = styled(Box)(({ theme }) => ({
  "& .ql-container, & .ql-toolbar": {
    transition: "border-color 0.2s ease-in-out",
  },
  "&:hover .ql-container, &:hover .ql-toolbar": {
    borderColor: theme.palette.primary.main,
  },
}));

const BioDisplayContainer = styled(Box)(({ theme }) => ({
  minHeight: "150px",
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  position: "relative",
  "& p": {
    margin: 0,
    lineHeight: 1.6,
  },
  "& p:not(:last-child)": {
    marginBottom: theme.spacing(1),
  },
  "& ul, & ol": {
    paddingLeft: theme.spacing(3),
    margin: `${theme.spacing(1)} 0`,
  },
  "& li": {
    marginBottom: theme.spacing(0.5),
  },
  "& a": {
    color: theme.palette.primary.main,
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

interface UserData {
  joinDate: string;
  profileBio: string;
  PreviewPic: string | null;
  displayName?: string;
  email?: string;
  phoneNumber?: string;
}

interface EditProfileProps {
  // Add any props if needed
}

function EditProfile(props: EditProfileProps) {
  const user = useSelector((state: RootState) => state.user.user);
  const [userData, setUserData] = useState<UserData>({
    joinDate: "",
    profileBio: "",
    PreviewPic: null,
  });
  const [loading, setLoading] = useState(true);
  const [bio, setBio] = useState("");
  const [originalBio, setOriginalBio] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getUserData();
  }, [user.uid]);

  const getUserData = async () => {
    try {
      setLoading(true);
      setError(null);

      const userDataRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(userDataRef);

      if (docSnap.exists()) {
        const data = docSnap.data() as UserData;
        setUserData(data);
        setBio(data.profileBio || "");
        setOriginalBio(data.profileBio || "");
      } else {
        setError("User profile not found. Please try refreshing the page.");
      }
    } catch (err) {
      console.error("Error fetching user data:", err);
      setError("Failed to load profile data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const saveBio = async () => {
    setError(null);
    setSuccess(null);
    setSaving(true);

    // Strip HTML tags for length calculation
    const textContent = bio.replace(/<[^>]*>/g, "");

    if (textContent.length > 1200) {
      setError("Please limit your bio to 1200 characters or less.");
      setSaving(false);
      return;
    }

    if (!bio.trim() || bio === "<p><br></p>") {
      setError("Bio cannot be empty. Please write something about yourself.");
      setSaving(false);
      return;
    }

    try {
      await updateDoc(doc(db, "users", user.uid), {
        profileBio: bio,
      });

      setUserData((prev) => ({ ...prev, profileBio: bio }));
      setOriginalBio(bio);
      setSuccess("Profile bio updated successfully!");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Error saving bio:", err);
      setError("Failed to save bio. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const getCharacterCount = () => {
    const textContent = bio.replace(/<[^>]*>/g, "");
    return textContent.length;
  };

  const hasChanges = () => {
    return bio !== originalBio;
  };

  const handleBioChange = (value: string) => {
    setBio(value);
    // Clear messages when user starts typing
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <PersonIcon color="primary" />
          Profile
        </Typography>
        <Grid2 container spacing={3}>
          <Grid2 size={{ xs: 12, md: 4 }}>
            <Skeleton
              variant="rectangular"
              height={350}
              sx={{ borderRadius: 2 }}
            />
          </Grid2>
          <Grid2 size={{ xs: 12, md: 8 }}>
            <Stack spacing={2}>
              <Skeleton variant="text" height={60} />
              <Skeleton variant="text" height={40} />
              <Skeleton variant="text" height={40} />
              <Skeleton variant="rectangular" height={200} />
            </Stack>
          </Grid2>
        </Grid2>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <PersonIcon color="primary" />
        Profile
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, md: 4 }}>
          <StyledCard>
            <CardContent>
              <UploadImage />
            </CardContent>
          </StyledCard>
        </Grid2>

        <Grid2 size={{ xs: 12, md: 8 }}>
          <StyledCard>
            <CardContent>
              <UserInfo userId={user.uid} />
            </CardContent>
          </StyledCard>

          <StyledCard>
            <CardContent>
              <Stack spacing={2}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="h6"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <EditIcon color="primary" />
                    Bio
                  </Typography>
                </Stack>

                <Divider />

                <BioContainer>
                  <TextEditor
                    text={bio}
                    handleChange={handleBioChange}
                    placeholder="Write something about yourself..."
                    maxLength={1200}
                    height={250}
                  />
                </BioContainer>

                <Stack spacing={2} alignItems="center">
                  <Typography
                    variant="body2"
                    color={
                      getCharacterCount() > 1100
                        ? "warning.main"
                        : "text.secondary"
                    }
                    sx={{ fontWeight: getCharacterCount() > 1100 ? 600 : 400 }}
                  >
                    {getCharacterCount()}/1200 characters
                    {getCharacterCount() > 1100 && " (approaching limit)"}
                  </Typography>

                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={
                      saving ? <CircularProgress size={16} /> : <SaveIcon />
                    }
                    onClick={saveBio}
                    disabled={!hasChanges() || saving}
                    sx={{
                      textTransform: "none",
                      fontWeight: 600,
                      minWidth: 120,
                    }}
                  >
                    {saving ? "Saving..." : "Save Bio"}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </StyledCard>
        </Grid2>
      </Grid2>
    </Box>
  );
}

export default EditProfile;
